import React, { useState, useRef } from 'react';
import { X, AlertCircle, Upload } from 'lucide-react';
import Button from './Button';
import Spinner from './Spinner';

// THE CONVERSION DIALOGS — add / edit one conversion, and upload a CSV of them. Shared by the sync task's Conversions
// screen (TaskConfigurationPage) and a pipeline implementation's Conversions tab (pipelines/PipelineConversionsTab),
// which copies that screen for a pipeline: one dialog, so a conversion is entered the same way wherever it lives.
//
//   conversionDataTypes  [{ scopeType, scopeDescription, fields: [{ field, description }] }]
//   conversion           { recordType, sourceFieldName, sourceValue, targetValue } to edit, or null to add
//   onSave(formData)     throws to show its message; resolves to keep going ("Save and add another")
//   isDefault            the Default Values variant — no source value, "Default Value" for the target
//
// Moved out of TaskConfigurationPage.js on 2026-11-04, text unchanged.

export function ConversionEditModal({ isOpen, onClose, onSave, conversion, conversionDataTypes, isDefault }) {
  const [formData, setFormData] = useState({
    recordType: conversion?.recordType || '',
    sourceFieldName: conversion?.sourceFieldName || '',
    sourceValue: conversion?.sourceValue || '',
    targetValue: conversion?.targetValue || ''
  });

  const fieldOptions = (conversionDataTypes || []).find(t => t.scopeType === formData.recordType)?.fields || [];
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [savedCount, setSavedCount] = useState(0);
  const sourceValueRef = useRef(null);

  // addAnother: after a successful save, keep the modal open and clear only the value fields
  // (retaining Record Type + Source Field Name) so the user can add many mappings for the same field.
  const submitConversion = async (addAnother) => {
    if (!formData.recordType || !formData.sourceFieldName || (!isDefault && !formData.sourceValue) || !formData.targetValue) {
      setError('All fields are required');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      await onSave(formData);
      if (addAnother) {
        setSavedCount(c => c + 1);
        setFormData({ ...formData, sourceValue: '', targetValue: '' });
        if (sourceValueRef.current) sourceValueRef.current.focus();
      } else {
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to save conversion');
    } finally {
      setSaving(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    submitConversion(false);
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{(conversion ? 'Edit ' : 'Add ') + (isDefault ? 'Default Value' : 'Conversion')}</h2>
          <button className="modal-close" onClick={onClose}>
            <X className="icon-sm" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            {error && (
              <div className="error-banner">
                <AlertCircle className="icon-sm" />
                {error}
              </div>
            )}
            
            <div className="form-group">
              <label className="form-label">Record Type *</label>
              <select
                className="form-input"
                value={formData.recordType}
                onChange={(e) => setFormData({ ...formData, recordType: e.target.value, sourceFieldName: '' })}
                required
              >
                <option value="">-- Select Record Type --</option>
                {(conversionDataTypes || []).map(type => (
                  <option key={type.scopeType} value={type.scopeType}>{type.scopeDescription}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">{isDefault ? 'Field *' : 'Source Field Name *'}</label>
              <select
                className="form-input"
                value={formData.sourceFieldName}
                onChange={(e) => setFormData({ ...formData, sourceFieldName: e.target.value })}
                required
                disabled={!formData.recordType}
              >
                <option value="">-- Select Field --</option>
                {fieldOptions.map(f => (
                  <option key={f.field} value={f.field}>{f.description}</option>
                ))}
              </select>
            </div>

            {!isDefault && (
              <div className="form-group">
                <label className="form-label">Source Value *</label>
                <input
                  ref={sourceValueRef}
                  type="text"
                  className="form-input"
                  value={formData.sourceValue}
                  onChange={(e) => setFormData({ ...formData, sourceValue: e.target.value })}
                  required
                />
              </div>
            )}

            <div className="form-group">
              <label className="form-label">{isDefault ? 'Default Value *' : 'Target Value *'}</label>
              <input
                type="text"
                className="form-input"
                value={formData.targetValue}
                onChange={(e) => setFormData({ ...formData, targetValue: e.target.value })}
                required
              />
            </div>
          </div>
          
          <div className="modal-footer">
            {savedCount > 0 && (
              <span className="conversion-saved-hint">{savedCount} saved</span>
            )}
            <Button variant="secondary" onClick={onClose} type="button" disabled={saving}>
              Cancel
            </Button>
            {!conversion && (
              <Button
                variant="secondary"
                type="button"
                onClick={() => submitConversion(true)}
                disabled={saving}
              >
                Save and add another
              </Button>
            )}
            <Button type="submit" disabled={saving}>
              {saving ? (
                <>
                  <Spinner size="sm" className="icon-sm" />
                  Saving...
                </>
              ) : (
                'Save Conversion'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

// `isDefault` only changes what the warning NAMES. It has to: the upload replaces one half of a shared
// table, and a banner that says "conversions" on the Default Values screen reads as a promise about the
// other task's rows. It was that wording, plus a server-side delete that really did take everything,
// that let people wipe their default values believing the warning did not apply to them.
export function ConversionCSVUploadModal({ isOpen, onClose, onUpload, isDefault = false }) {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!file || !confirmed) return;

    try {
      setUploading(true);
      await onUpload(file);
      onClose();
    } catch (err) {
      // Error handled by parent
    } finally {
      setUploading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Upload {isDefault ? 'Default Values' : 'Conversions'} CSV</h2>
          <button className="modal-close" onClick={onClose}>
            <X className="icon-sm" />
          </button>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="error-banner" style={{ background: '#fff7ed', borderColor: '#fdba74', color: '#9a3412' }}>
              <AlertCircle className="icon-sm" />
              This will delete all existing {isDefault ? 'default values' : 'conversions'} on this project and
              replace them with the contents of the uploaded file.
              {' '}{isDefault ? 'Conversions' : 'Default values'} are left alone.
            </div>
            <div className="form-group" style={{ marginTop: '1rem' }}>
              <label className="form-label">CSV File *</label>
              <input
                type="file"
                accept=".csv"
                className="form-input"
                onChange={(e) => setFile(e.target.files[0])}
                required
              />
              <span className="form-hint">
                CSV must include columns: recordType, sourceFieldName, sourceValue, targetValue.
                {isDefault
                  ? ' A default value is a row with an empty sourceValue; rows that have one are ignored.'
                  : ' Rows with an empty sourceValue are default values and are ignored.'}
              </span>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '0.875rem', cursor: 'pointer' }}>
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} />
              I understand all existing {isDefault ? 'default values' : 'conversions'} will be replaced
            </label>
          </div>

          <div className="modal-footer">
            <Button variant="secondary" onClick={onClose} type="button" disabled={uploading}>
              Cancel
            </Button>
            <Button type="submit" disabled={uploading || !file || !confirmed}>
              {uploading ? (
                <>
                  <Spinner size="sm" className="icon-sm" />
                  Uploading...
                </>
              ) : (
                <>
                  <Upload className="icon-sm" />
                  Upload
                </>
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
